import { useEffect, useRef, useState } from "react";

import { ChipDownContainer, Checkbox, PlainTooltip } from "@similarweb/ui-components";

import { FILTER_WIDTH, KEYS } from "app/pages/website-analysis/website-technologies/constants/constants";
import { FooterButton } from "pro-features/components/MultiCategoriesChipDown/src/MultiCategoryChipdownStyles";
import { formatPercentageString } from "app/components/React/Table/cells/OrganicPaid";
import { useTranslation } from "pro-features/components/WithTranslation/src/I18n";
import { getNthObjectKey, isNotEmptyObject } from "app/pages/workspace/sales/utils/object";

import {
    StyledTextContainer,
    StyledCheckboxContainer,
    StyledItemInnerWrapper,
    StyledEllipsisDropdownItem,
    StyledTrafficContainer,
} from "../styles";
import type { SubDomainFilterProps } from "../types";

const SELECT_ALL_BTN_ID = "allItemsSelectBtn";

export const SubDomainFilter = ({
    onSelect,
    data,
    activeWebsite,
    onClear,
    clearOnActiveWebsiteChange,
    disabled,
    appendTo,
    headerComponent,
}: SubDomainFilterProps) => {
    const translate = useTranslation();

    const [showApply, setShowApply] = useState(false);
    const [applyDisabled, setApplyDisabled] = useState(true);
    const [unselectedList, setUnselectedList] = useState<string[]>([]);
    const [unApplied, setUnApplied] = useState<string[]>([]);
    const [selectedIds, setSelectedIds] = useState<Record<string, boolean>>();
    const [numOfSelectedSubDomains, setNumOfSelectedSubDomains] = useState<number>(0);
    const [selectedName, setSelectedName] = useState<string>("");

    const chipDownRef = useRef(undefined);

    useEffect(() => {
        const optionsList = Object.keys(data);
        const numOfSelectedIds = optionsList.length - unselectedList.length;

        setNumOfSelectedSubDomains(numOfSelectedIds);

        const ids = optionsList.reduce((acc, item) => {
            if (unselectedList.includes(item)) {
                return acc;
            }
            acc[item] = true;
            return acc;
        }, {});

        setSelectedIds(ids);
    }, [unselectedList, data]);

    useEffect(() => {
        if (isNotEmptyObject(selectedIds)) {
            setApplyDisabled(false);
        } else {
            setApplyDisabled(true);
        }
    }, [selectedIds]);

    useEffect(() => {
        if (clearOnActiveWebsiteChange && activeWebsite) {closeHandler();}
    }, [clearOnActiveWebsiteChange, activeWebsite]);

    const clickHandler = (item: { id: string; text: string }) => {
        setShowApply(true);
        const internalUnselected = [...unselectedList];

        if (item.id === SELECT_ALL_BTN_ID) {
            unselectedList.length ? setUnselectedList([]) : setUnselectedList(Object.keys(data));
            return;
        }
        if (internalUnselected.includes(item?.id)) {
            const deleteIndex = internalUnselected.findIndex((unselectedItem) => unselectedItem === item?.id);
            internalUnselected.splice(deleteIndex, 1);
            setUnselectedList(internalUnselected);
            return;
        }

        internalUnselected.push(item?.id);
        setUnselectedList(internalUnselected);
        return;
    };

    const closeHandler = () => {
        onClear();
        setUnselectedList([]);
        setSelectedName("");
    };

    const toggleHandler = (_isOpen: boolean, isOutsideClick: boolean) => {
        if (showApply && isOutsideClick) {
            if (unselectedList.length !== unApplied.length) {
                setUnselectedList(unApplied);
            } else {
                setUnselectedList([]);
            }
        }
        setShowApply(false);
    };

    const applyHandler = () => {
        onSelect(Object.keys(selectedIds));
        setUnApplied(unselectedList);

        const firstSelected = getNthObjectKey(selectedIds, 0);

        const selectedText = firstSelected
            ? `${firstSelected} ${numOfSelectedSubDomains > 1 ? `+${numOfSelectedSubDomains - 1}` : ""}`
            : ``;

        setSelectedName(selectedText);
        chipDownRef?.current?.closePopup();
    };

    const renderOptions = () => {
        const selectedAll = !unselectedList.length;
        const dataKeys = Object.keys(data);

        return [
            <StyledEllipsisDropdownItem
                selected={selectedAll}
                key={SELECT_ALL_BTN_ID}
                id={SELECT_ALL_BTN_ID}
                className="CustomCheckboxDropdownItem"
            >
                <StyledItemInnerWrapper>
                    <StyledTextContainer>
                        {translate("analysis.filters.subdomain.filter", { numOfSelectedSubDomains: dataKeys.length })}
                    </StyledTextContainer>
                    <StyledCheckboxContainer>
                        <Checkbox
                            label=""
                            onClick={null}
                            selected={selectedAll}
                        />
                    </StyledCheckboxContainer>
                </StyledItemInnerWrapper>
            </StyledEllipsisDropdownItem>,
            ...dataKeys.map((name) => {
                const selected = unselectedList.includes(name);
                return (
                    <StyledEllipsisDropdownItem
                        selected={!selected}
                        key={name}
                        id={name}
                        text={name}
                        className="CustomCheckboxDropdownItem"
                    >
                        <StyledItemInnerWrapper>
                            <StyledTextContainer>
                                {name.length > 27 ? (
                                    <PlainTooltip
                                        maxWidth={260}
                                        placement="top"
                                        tooltipContent={name}
                                    >
                                        <span>{`${name}`}</span>
                                    </PlainTooltip>
                                ) : (
                                    name
                                )}
                            </StyledTextContainer>
                            {headerComponent && (
                                <StyledTrafficContainer>
                                    {formatPercentageString(data[name]?.share)}
                                </StyledTrafficContainer>
                            )}
                            <StyledCheckboxContainer>
                                <Checkbox
                                    label=""
                                    onClick={null}
                                    selected={!selected}
                                />
                            </StyledCheckboxContainer>
                        </StyledItemInnerWrapper>
                    </StyledEllipsisDropdownItem>
                );
            }),
        ];
    };

    return (
        <ChipDownContainer
            appendTo={appendTo}
            onToggle={toggleHandler}
            disabled={disabled}
            ref={chipDownRef}
            onClick={clickHandler}
            selectedIds={selectedIds}
            selectedText={selectedName}
            closeOnItemClick={false}
            buttonText={translate("analysis.filters.subdomain.filter", { numOfSelectedSubDomains })}
            searchPlaceHolder={translate(KEYS.subdomainPlaceholder)}
            onCloseItem={closeHandler}
            hasSearch={false}
            width={FILTER_WIDTH}
            tooltipDisabled
            headerComponent={headerComponent}
            noResultsMessage={translate("dropdown.no.results")}
            footerComponent={() =>
                showApply && (
                    <FooterButton
                        onClick={applyHandler}
                        disabled={applyDisabled}
                    >
                        {translate(KEYS.subdomainBtnApply)}
                    </FooterButton>
                )
            }
        >
            {renderOptions()}
        </ChipDownContainer>
    );
};
